import { useState, useEffect, useRef } from "react";
import {
	DEFAULT_LOONSOM,
	DEFAULT_VERZUIM,
	MAX_LOONSOM,
	MAX_VERZUIM,
	MIN_LOONSOM,
	MIN_VERZUIM,
	STAP_LOONSOM,
	STAP_VERZUIM,
	VERZUIM_PRESETS,
	VERZUIM_PRESETS_BRON,
	zichtbarePresets,
	berekenVerzuimkosten,
	margePerFiets,
	aantalEenheden,
	formatEuro,
	formatGetal,
	formatPercentage,
} from "./berekening.js";
import { DEFAULT_COPY } from "./copy.js";

/**
 * Alpina verzuimkostencalculator — embed-variant.
 *
 * Laat een werkgever zien wat loondoorbetaling bij verzuim over twee jaar kost.
 *
 * Verschillen t.o.v. de referentie-JSX:
 *  - Merkkleuren gecorrigeerd naar de Alpina huisstijl (Petrol/Oranje/Hardroze/
 *    Cream/Sky) i.p.v. de benaderingen uit het bouwdocument (decision #14).
 *  - CTA-knop is Hardroze (huisstijl: "Hardroze enkel voor actie/CTA").
 *  - Fonts: Prompt (headings) + Etelka Text Pro (body), zelf-gehost via thema.
 *  - Typografie afgestemd op de pagina-schaal: koppen Prompt Medium 500,
 *    body 16px (base) / 13px (sm), sectiekop groter (consistent met de blokken).
 *  - Copy komt uit Gutenberg-attributen (defaults in block.json); de editor
 *    injecteert RichText via de `Text`-prop.
 *  - Verzendt naar de WP REST proxy (decision #7) i.p.v. console.log.
 *    De premie wordt server-side berekend en NIET getoond.
 *  - Alle rekenlogica, grenswaarden en presets komen uit berekening.js.
 */

// Huisstijl (campagne-styleguide jan. 2026)
const PETROL = "#0A364B";
const ORANGE = "#F39200";
const HARDROZE = "#E50053";
const CREAM = "#FFF4D7";
const WHITE = "#FFFFFF";
const MUTED = "#7FA8CC";
// MUTED (#7FA8CC) haalt op lichte vlakken geen 4.5:1. Voor kleine, "stille"
// tekst daarom deze twee tinten: SUBTLE op wit/sky (4.9:1 resp. 6.2:1) en
// MUTED_ON_DARK op het petrol-resultaatvlak (5.7:1).
const SUBTLE = "#55636F";
const MUTED_ON_DARK = "#9DC0DE";
const GRAY = "#5B6B79";
const BORDER = "#D7E2EA";
const GREEN = "#059669";

const FONT_HEAD = "'Prompt', system-ui, -apple-system, sans-serif";
const FONT_BODY = "'Etelka Text Pro', system-ui, -apple-system, sans-serif";

// Typografie-schaal, consistent met het thema (theme.json): koppen Prompt Medium
// 500, body Etelka 16px (base) / 20px (lg) / 13px (sm). Zo sluit de calculator
// aan op de rest van de pagina.
const HEAD_WEIGHT = 500; // thema heading-gewicht (Medium)

function escapeHtml(value) {
	return String(value)
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

/** Vervangt `{email}` in de bevestigingstekst. De waarde zelf wordt ge-escaped. */
export function interpolateEmail(template, email) {
	return String(template || "").replace(/\{email\}/g, `<strong>${escapeHtml(email)}</strong>`);
}

/**
 * Frontend-weergave van Gutenberg-copy. `name` / `allowedFormats` zijn no-ops
 * hier; de editor vervangt dit component door RichText.
 */
export function PlainText({ tagName: Tag = "span", value, style, className }) {
	if (value == null || value === "") return null;
	return <Tag className={className} style={style} dangerouslySetInnerHTML={{ __html: value }} />;
}

/**
 * Telt een waarde naar het nieuwe getal toe. `format` bepaalt de weergave,
 * zodat het bedrag tijdens de animatie in euro-notatie blijft staan.
 */
function AnimatedNumber({ value, format = (n) => n }) {
	const [display, setDisplay] = useState(value);
	const prev = useRef(value);
	const raf = useRef(null);

	useEffect(() => {
		if (raf.current) cancelAnimationFrame(raf.current);
		const start = prev.current;
		const end = value;
		const duration = 350;
		const t0 = performance.now();
		const tick = (now) => {
			const p = Math.min((now - t0) / duration, 1);
			const ease = 1 - Math.pow(1 - p, 3);
			setDisplay(Math.round(start + (end - start) * ease));
			if (p < 1) raf.current = requestAnimationFrame(tick);
			else prev.current = end;
		};
		raf.current = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf.current);
	}, [value]);

	return <>{format(display)}</>;
}

/** Zelfde Twemoji-fiets als in de oorspronkelijke landingspagina. */
function BikeEmoji() {
	return (
		<img
			draggable="false"
			role="presentation"
			className="emoji"
			alt=""
			src="https://s.w.org/images/core/emoji/17.0.2/svg/1f6b2.svg"
		/>
	);
}

export default function VerzuimCalculator({
	restUrl = "",
	nonce = "",
	preview = false,
	editing = false,
	copy = {},
	presets,
	showYearlyRisk = false,
	showContextNote = false,
	fietsPrijs,
	fietsMargePercent,
	Text = PlainText,
}) {
	const t = {
		...DEFAULT_COPY,
		...Object.fromEntries(Object.entries(copy).filter(([, value]) => typeof value === "string")),
	};
	const T = Text;
	const knoppen = Array.isArray(presets) ? zichtbarePresets(presets) : VERZUIM_PRESETS;
	const startVerzuim = knoppen[0]?.percentage ?? DEFAULT_VERZUIM;

	const [loonsom, setLoonsom] = useState(DEFAULT_LOONSOM);
	const [verzuim, setVerzuim] = useState(startVerzuim);
	const [loonsomRaw, setLoonsomRaw] = useState(formatGetal(DEFAULT_LOONSOM));
	const [actievePreset, setActievePreset] = useState(null);
	const [heeftVerzekering, setHeeftVerzekering] = useState(null);
	const [email, setEmail] = useState("");
	const [submitted, setSubmitted] = useState(false);
	const [sending, setSending] = useState(false);
	const [error, setError] = useState("");

	const { totaleKostenTweeJaar, basisJaarkosten } = berekenVerzuimkosten(loonsom, verzuim);
	const fietsWeergave = showContextNote === true;
	const perFiets = margePerFiets(fietsPrijs, fietsMargePercent);
	const fietsenTweeJaar = aantalEenheden(totaleKostenTweeJaar, perFiets);
	const fietsenPerJaar = aantalEenheden(Math.round(basisJaarkosten), perFiets);
	const jaarKosten = Math.round(basisJaarkosten);

	const emailValid = /\S+@\S+\.\S+/.test(email);

	const syncLoonsom = (val) => {
		setLoonsom(val);
		setLoonsomRaw(formatGetal(val));
	};

	const handleLoonsomText = (e) => {
		const raw = e.target.value.replace(/\./g, "").replace(/\D/g, "");
		const n = Math.min(parseInt(raw) || 0, MAX_LOONSOM);
		setLoonsom(n);
		setLoonsomRaw(e.target.value);
	};

	// Handmatig schuiven telt als eigen invoer: de presetselectie vervalt.
	const handleVerzuimSlider = (e) => {
		setVerzuim(parseFloat(e.target.value));
		setActievePreset(null);
	};

	const kiesPreset = (preset) => {
		setVerzuim(preset.percentage);
		setActievePreset(preset.id);
	};

	const handleSubmit = async () => {
		if (editing || !emailValid || sending) return;
		setError("");
		if (preview || !restUrl) {
			// Editor-preview of geen endpoint: toon bevestiging zonder POST.
			setSubmitted(true);
			return;
		}
		setSending(true);
		try {
			const res = await fetch(restUrl, {
				method: "POST",
				headers: { "Content-Type": "application/json", "X-WP-Nonce": nonce },
				body: JSON.stringify({ email, loonsom, verzuim, heeftVerzekering: !!heeftVerzekering }),
			});
			if (!res.ok) {
				const data = await res.json().catch(() => ({}));
				throw new Error(data?.message || "Er ging iets mis. Probeer het opnieuw.");
			}
			setSubmitted(true);
		} catch (err) {
			setError(err.message || "Er ging iets mis. Probeer het later opnieuw.");
		} finally {
			setSending(false);
		}
	};

	const card = {
		background: WHITE,
		borderRadius: "16px",
		padding: "28px",
		boxShadow: "0 2px 16px rgba(10,54,75,0.07)",
		marginBottom: "16px",
	};
	const labelStyle = { fontSize: "16px", fontWeight: 600, color: PETROL, fontFamily: FONT_BODY };
	const pillStyle = {
		background: CREAM,
		borderRadius: "8px",
		padding: "6px 12px",
		border: `1px solid ${BORDER}`,
		fontSize: "15px",
		fontWeight: 700,
		color: PETROL,
		whiteSpace: "nowrap",
		flex: "none",
	};
	// Label + waarde-pill: wrapt op smalle schermen i.p.v. over te lopen.
	const rowStyle = {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		gap: "12px",
		flexWrap: "wrap",
		marginBottom: "14px",
	};
	const rangeStyle = { width: "100%", accentColor: ORANGE, cursor: "pointer" };
	const scaleStyle = {
		display: "flex",
		justifyContent: "space-between",
		fontSize: "13px",
		color: SUBTLE,
		marginTop: "6px",
	};

	return (
		<div style={{ fontFamily: FONT_BODY, color: PETROL, fontSize: "16px", lineHeight: 1.6 }}>
			<div style={{ maxWidth: "560px", margin: "0 auto" }}>
				<div style={{ marginBottom: "24px" }}>
					<h2
						className="avc-heading"
						style={{ fontFamily: FONT_HEAD, fontSize: "clamp(26px, 5.5vw, 32px)", fontWeight: HEAD_WEIGHT, color: PETROL, margin: "0 0 12px", lineHeight: 1.2, letterSpacing: "-0.5px", textWrap: "balance" }}
					>
						<T name="heading" tagName="span" value={t.heading} allowedFormats={["core/bold"]} />
					</h2>
					<p style={{ color: GRAY, fontSize: "16px", margin: 0, lineHeight: 1.6 }}>
						<T name="intro" tagName="span" value={t.intro} allowedFormats={["core/bold"]} />
					</p>
				</div>

				{/* Inputs */}
				<div style={card}>
					<div style={{ marginBottom: "28px" }}>
						<div style={rowStyle}>
							{editing ? (
								<div style={labelStyle}>
									<T name="labelLoonsom" tagName="span" value={t.labelLoonsom} allowedFormats={[]} />
								</div>
							) : (
								<label htmlFor="avc-loonsom" style={labelStyle}>
									<PlainText tagName="span" value={t.labelLoonsom} />
								</label>
							)}
							<div style={{ ...pillStyle, display: "flex", alignItems: "center", gap: "3px" }}>
								<span style={{ color: GRAY, fontSize: "14px" }}>€</span>
								<input
									value={loonsomRaw}
									onChange={handleLoonsomText}
									onBlur={() => setLoonsomRaw(formatGetal(loonsom))}
									inputMode="numeric"
									aria-label="Jaarloonsom in euro's"
									style={{ border: "none", background: "transparent", width: "88px", maxWidth: "100%", fontSize: "15px", fontWeight: 700, color: PETROL, outline: "none", textAlign: "right", padding: 0, fontFamily: FONT_BODY }}
								/>
							</div>
						</div>
						<input type="range" id="avc-loonsom" min={MIN_LOONSOM} max={MAX_LOONSOM} step={STAP_LOONSOM} value={loonsom}
							onChange={(e) => syncLoonsom(parseInt(e.target.value))}
							aria-label="Jaarloonsom"
							aria-valuetext={formatEuro(loonsom)}
							style={rangeStyle} />
						<div style={scaleStyle}>
							<span>{formatEuro(MIN_LOONSOM)}</span><span>{formatEuro(MAX_LOONSOM)}</span>
						</div>
					</div>

					<div>
						<div style={rowStyle}>
							{editing ? (
								<div style={labelStyle}>
									<T name="labelVerzuim" tagName="span" value={t.labelVerzuim} allowedFormats={[]} />
								</div>
							) : (
								<label htmlFor="avc-verzuim" style={labelStyle}>
									<PlainText tagName="span" value={t.labelVerzuim} />
								</label>
							)}
							<div style={pillStyle}>{formatPercentage(verzuim)} %</div>
						</div>
						<input type="range" id="avc-verzuim" min={MIN_VERZUIM} max={MAX_VERZUIM} step={STAP_VERZUIM} value={verzuim}
							onChange={handleVerzuimSlider}
							aria-label="Verzuimpercentage"
							aria-valuetext={`${formatPercentage(verzuim)} procent`}
							style={rangeStyle} />
						<div style={scaleStyle}>
							<span>{formatPercentage(MIN_VERZUIM)}%</span><span>{MAX_VERZUIM}%</span>
						</div>

						{/* Keuzehulp: optionele presets. De slider blijft direct bruikbaar. */}
						{knoppen.length > 0 && (
							<div style={{ marginTop: "18px" }}>
								<div id="avc-preset-hint" style={{ fontSize: "13px", color: GRAY, marginBottom: "8px" }}>
									<T name="presetHint" tagName="span" value={t.presetHint} allowedFormats={[]} />
								</div>
								<div className="avc-presets" role="group" aria-labelledby="avc-preset-hint">
									{knoppen.map((preset) => {
										const actief = actievePreset === preset.id;
										return (
											<button
												key={preset.id}
												type="button"
												className="avc-preset"
												aria-pressed={actief}
												onClick={() => kiesPreset(preset)}
												style={{
													border: `2px solid ${actief ? ORANGE : BORDER}`,
													background: actief ? CREAM : WHITE,
													color: actief ? PETROL : GRAY,
													fontWeight: actief ? 700 : 500,
													fontFamily: FONT_BODY,
												}}
											>
												{preset.label} <span style={{ color: actief ? PETROL : SUBTLE }}>{formatPercentage(preset.percentage)}%</span>
											</button>
										);
									})}
								</div>
								<p style={{ fontSize: "12px", color: SUBTLE, margin: "8px 0 0", lineHeight: 1.5 }}>
									<T name="presetBron" tagName="span" value={t.presetBron || VERZUIM_PRESETS_BRON} allowedFormats={["core/bold"]} />
								</p>
							</div>
						)}
					</div>
				</div>

				{/* Resultaat */}
				<div
					style={{
						background: PETROL,
						borderRadius: "16px",
						padding: "28px",
						marginBottom: "16px",
						...(fietsWeergave ? { position: "relative", overflow: "hidden" } : {}),
					}}
				>
					{fietsWeergave && (
						<div
							aria-hidden="true"
							style={{
								position: "absolute",
								right: "16px",
								bottom: "-8px",
								fontSize: "96px",
								opacity: 0.05,
								lineHeight: 1,
								userSelect: "none",
							}}
						>
							<BikeEmoji />
						</div>
					)}
					<div style={{ color: MUTED, fontSize: "13px", textTransform: "uppercase", letterSpacing: "2px", marginBottom: "18px" }}>
						<T name="resultEyebrow" tagName="span" value={t.resultEyebrow} allowedFormats={[]} />
					</div>
					{fietsWeergave ? (
						<>
							<div
								style={{
									background: "rgba(255, 255, 255, 0.07)",
									borderRadius: "12px",
									padding: "20px",
									display: "flex",
									alignItems: "center",
									gap: "18px",
									marginBottom: "16px",
								}}
							>
								<div aria-hidden="true" style={{ fontSize: "44px", lineHeight: 1, flexShrink: 0 }}>
									<BikeEmoji />
								</div>
								<div>
									<div style={{ color: MUTED, fontSize: "15px", marginBottom: "4px" }}>
										<T name="fietsHeroPrefix" tagName="span" value={t.fietsHeroPrefix} allowedFormats={[]} />
									</div>
									<div style={{ color: WHITE, fontSize: "36px", fontWeight: 700, letterSpacing: "-1px", lineHeight: 1, fontFamily: FONT_HEAD }}>
										<AnimatedNumber value={fietsenTweeJaar} format={formatGetal} />{" "}
										<T
											name="fietsUnit"
											tagName="span"
											value={t.fietsUnit || "fietsen"}
											allowedFormats={[]}
											style={{ fontSize: "22px", fontWeight: 500 }}
										/>
									</div>
									<div style={{ color: MUTED, fontSize: "13px", marginTop: "4px" }}>
										<T name="fietsHeroSuffix" tagName="span" value={t.fietsHeroSuffix} allowedFormats={[]} />
									</div>
									<div style={{ color: MUTED, fontSize: "13px", marginTop: "6px", opacity: 0.75 }}>
										<T name="fietsFootnote" tagName="span" value={t.fietsFootnote} allowedFormats={["core/bold"]} />
									</div>
								</div>
							</div>
							<div className="avc-fiets-split" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
								<div style={{ background: "rgba(255, 255, 255, 0.06)", borderRadius: "10px", padding: "14px" }}>
									<div style={{ color: MUTED, fontSize: "13px", marginBottom: "6px" }}>
										<T name="resultTotalLabel" tagName="span" value={t.resultTotalLabel} allowedFormats={[]} />
									</div>
									<div style={{ color: ORANGE, fontSize: "22px", fontWeight: 700, fontFamily: FONT_HEAD }}>
										<AnimatedNumber value={totaleKostenTweeJaar} format={formatEuro} />
									</div>
								</div>
								<div style={{ background: "rgba(255, 255, 255, 0.06)", borderRadius: "10px", padding: "14px" }}>
									<div style={{ color: MUTED, fontSize: "13px", marginBottom: "6px" }}>
										<T name="resultYearlyLabel" tagName="span" value={t.resultYearlyLabel || "Per jaar"} allowedFormats={[]} />
									</div>
									<div style={{ color: WHITE, fontSize: "22px", fontWeight: 700, fontFamily: FONT_HEAD }}>
										<AnimatedNumber value={jaarKosten} format={formatEuro} />
									</div>
									<div style={{ color: MUTED, fontSize: "13px", marginTop: "2px" }}>
										≈ <AnimatedNumber value={fietsenPerJaar} format={formatGetal} /> {t.fietsUnit || "fietsen"}
									</div>
								</div>
							</div>
						</>
					) : (
						<div style={{ background: "rgba(255,255,255,0.07)", borderRadius: "12px", padding: "24px" }}>
							{showYearlyRisk === true && (
								<div style={{ marginBottom: "18px", paddingBottom: "18px", borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
									<div style={{ color: MUTED_ON_DARK, fontSize: "15px", marginBottom: "6px" }}>
										<T name="resultYearlyLabel" tagName="span" value={t.resultYearlyLabel || "Per jaar"} allowedFormats={[]} />
									</div>
									<div style={{ color: ORANGE, fontSize: "clamp(26px, 7vw, 34px)", fontWeight: 700, letterSpacing: "-1px", lineHeight: 1.1, fontFamily: FONT_HEAD, overflowWrap: "anywhere" }}>
										<AnimatedNumber value={jaarKosten} format={formatEuro} />
									</div>
								</div>
							)}
							<div style={{ color: MUTED_ON_DARK, fontSize: "15px", marginBottom: "6px" }}>
								<T name="resultLabel" tagName="span" value={t.resultLabel} allowedFormats={[]} />
							</div>
							<div style={{ color: ORANGE, fontSize: "clamp(34px, 9vw, 44px)", fontWeight: 700, letterSpacing: "-1px", lineHeight: 1.1, fontFamily: FONT_HEAD, overflowWrap: "anywhere" }}>
								<AnimatedNumber value={totaleKostenTweeJaar} format={formatEuro} />
							</div>
							<div style={{ color: MUTED_ON_DARK, fontSize: "13px", marginTop: "10px", lineHeight: 1.6 }}>
								<T name="resultNote" tagName="span" value={t.resultNote} allowedFormats={["core/bold"]} />
							</div>
						</div>
					)}
				</div>

				{/* CTA */}
				<div style={card}>
					{!submitted ? (
						<>
							<h3 style={{ fontFamily: FONT_HEAD, fontSize: "20px", fontWeight: HEAD_WEIGHT, color: PETROL, margin: "0 0 8px" }}>
								<T name="ctaHeading" tagName="span" value={t.ctaHeading} allowedFormats={[]} />
							</h3>
							<p style={{ fontSize: "15px", color: GRAY, margin: "0 0 18px", lineHeight: 1.6 }}>
								<T name="ctaIntro" tagName="span" value={t.ctaIntro} allowedFormats={["core/bold"]} />
							</p>

							<div style={{ marginBottom: "18px" }}>
								<div style={{ fontSize: "15px", fontWeight: 600, color: PETROL, marginBottom: "10px" }}>
									<T name="labelVerzekering" tagName="span" value={t.labelVerzekering} allowedFormats={[]} />
								</div>
								<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
									{[
										{ val: false, name: "labelVerzekeringNee", label: t.labelVerzekeringNee },
										{ val: true, name: "labelVerzekeringJa", label: t.labelVerzekeringJa },
									].map(({ val, name, label }) => {
										const toggleStyle = {
											padding: "12px",
											borderRadius: "8px",
											border: `2px solid ${heeftVerzekering === val ? ORANGE : BORDER}`,
											background: heeftVerzekering === val ? "#FFF7EC" : WHITE,
											color: heeftVerzekering === val ? ORANGE : GRAY,
											fontSize: "15px",
											fontWeight: heeftVerzekering === val ? 700 : 500,
											fontFamily: FONT_BODY,
											cursor: editing ? "text" : "pointer",
											transition: "all 0.15s ease",
											textAlign: "center",
										};
										return editing ? (
											<div key={String(val)} className="avc-toggle" style={toggleStyle}>
												<T name={name} tagName="span" value={label} allowedFormats={[]} />
											</div>
										) : (
											<button key={String(val)} type="button" className="avc-toggle" aria-pressed={heeftVerzekering === val} onClick={() => setHeeftVerzekering(val)} style={toggleStyle}>
												{label}
											</button>
										);
									})}
								</div>
							</div>

							<input
								type="email"
								placeholder={t.emailPlaceholder}
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
								aria-label="E-mailadres"
								style={{
									width: "100%", padding: "14px 16px", borderRadius: "10px",
									border: `1.5px solid ${BORDER}`, fontSize: "16px", color: PETROL,
									outline: "none", marginBottom: "12px", boxSizing: "border-box",
									background: "#F7FAFC", fontFamily: FONT_BODY,
								}}
							/>

							{error && (
								<p role="alert" style={{ color: HARDROZE, fontSize: "14px", margin: "0 0 12px" }}>{error}</p>
							)}

							{editing ? (
								<div
									className="avc-submit"
									style={{
										width: "100%", padding: "15px",
										background: HARDROZE,
										color: WHITE, border: "none", borderRadius: "999px",
										fontSize: "16px", fontWeight: 600, fontFamily: FONT_BODY,
										textAlign: "center",
										boxSizing: "border-box",
										boxShadow: "0 4px 12px rgba(229,0,83,0.3)",
									}}
								>
									<T name="submitLabel" tagName="span" value={t.submitLabel} allowedFormats={[]} />
								</div>
							) : (
								<button
									type="button"
									className="avc-submit"
									onClick={handleSubmit}
									disabled={!emailValid || sending}
									style={{
										width: "100%", padding: "15px",
										background: emailValid && !sending ? HARDROZE : "#D1D5DB",
										color: WHITE, border: "none", borderRadius: "999px",
										fontSize: "16px", fontWeight: 600, fontFamily: FONT_BODY,
										cursor: emailValid && !sending ? "pointer" : "default",
										transition: "background 0.15s ease",
										boxShadow: emailValid && !sending ? "0 4px 12px rgba(229,0,83,0.3)" : "none",
									}}
								>
									{sending ? t.submitSending : t.submitLabel}
								</button>
							)}

							<p style={{ textAlign: "center", fontSize: "13px", color: SUBTLE, margin: "12px 0 0" }}>
								<T name="ctaFooter" tagName="span" value={t.ctaFooter} allowedFormats={[]} />
							</p>
						</>
					) : (
						<div style={{ textAlign: "center", padding: "12px 0" }}>
							<div style={{ fontSize: "36px", marginBottom: "12px" }} aria-hidden="true">✉️</div>
							<h3 style={{ fontFamily: FONT_HEAD, fontSize: "20px", fontWeight: HEAD_WEIGHT, color: GREEN, margin: "0 0 8px" }}>
								<PlainText tagName="span" value={t.successHeading} />
							</h3>
							<p style={{ fontSize: "16px", color: GRAY, lineHeight: 1.65, margin: "0 0 16px" }}>
								<PlainText tagName="span" value={interpolateEmail(t.successBody, email)} />
							</p>
							<div style={{ background: CREAM, borderRadius: "10px", padding: "14px 16px", borderLeft: `3px solid ${ORANGE}` }}>
								<p style={{ margin: 0, fontSize: "14px", color: PETROL, lineHeight: 1.6 }}>
									<PlainText
										tagName="span"
										value={heeftVerzekering ? t.successWithInsurance : t.successWithoutInsurance}
									/>
								</p>
							</div>
						</div>
					)}
				</div>

				<p style={{ fontSize: "13px", color: SUBTLE, textAlign: "center", margin: "4px 0 0", lineHeight: 1.7 }}>
					<T name="disclaimer" tagName="span" value={t.disclaimer} allowedFormats={["core/bold"]} />
				</p>
			</div>
		</div>
	);
}
